<template>
    <button 
        :type="type"
        :disabled="disabled"
        :class="buttonClasses"
    >
        <template v-if="variant === 'split' && (leftIcon || icon) && rightIcon">
            <div class="flex items-center h-full -ml-2">
                <i :class="leftIcon || icon"></i>
                <span class="w-px bg-white ml-2 self-stretch"></span>
            </div>
            <span class="mx-4"><slot></slot></span>
            <div class="flex items-center h-full -mr-2">
                <span class="w-px bg-white mr-2 self-stretch"></span>
                <i :class="rightIcon"></i>
            </div>
        </template>
        <template v-else>
            <i v-if="leftIcon || icon" :class="leftIcon || icon" class="mr-2"></i>
            <slot></slot>
            <i v-if="rightIcon" :class="rightIcon" class="ml-2"></i>
        </template>
    </button>
</template>

<script setup lang="ts">
import { computed } from 'vue';

interface Props {
    variant?: 'primary' | 'flat' | 'secondary' | 'secondaryFlat' | 'secondaryBorderHover' | 'outline' | 'outlineMobile' | 'tertiary' | 'deliveryAction' | 'highlights' | 'split';
    size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl' | 'custom';
    disabled?: boolean;
    type?: 'button' | 'submit' | 'reset';
    icon?: string;
    leftIcon?: string;
    rightIcon?: string;
    fullWidth?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
    variant: 'primary',
    size: 'md',
    disabled: false,
    type: 'button',
    icon: '',
    leftIcon: '',
    rightIcon: '',
    fullWidth: false
});

// Variant styles
// Border width/style and font-weight live here (not in the shared base classes) so each
// variant can match its real-world look without callers needing per-usage `!` overrides.
const variantClasses = computed(() => {
    const variants = {
        primary: 'bg-brand-primary border border-brand-primary font-medium text-white hover:bg-brand-primary-hover active:bg-brand-primary-active',

        //This section is only for demonstration purposes. The hover, active, disabled, and focus states are handled by the primary variant.
        primaryHover: 'bg-brand-primary-hover border border-brand-primary-hover font-medium text-white hover:bg-brand-primary-hover active:bg-brand-primary-active',
        primaryActive: 'bg-brand-primary-hover border border-brand-primary-hover font-medium text-white hover:bg-brand-primary-hover active:bg-brand-primary-active',
        primaryDisabled: 'bg-btn-border border border-btn-border-strong font-medium text-btn-text-disabled hover:bg-btn-border active:bg-btn-border shadow-btn-disabled-inset',
        primaryFocus: 'bg-brand-primary border !border-brand-focus font-medium text-white hover:bg-brand-primary-hover active:bg-brand-primary-active shadow-btn-focus-ring',
        //Primary button demonstration section end here

        // Same brand color as primary but borderless/font-normal (e.g. compact dashboard "create new" buttons).
        flat: 'bg-brand-primary border-0 font-normal text-white hover:bg-brand-primary-hover active:bg-brand-primary-active',

        secondary: 'bg-white border border-btn-border font-normal text-btn-text hover:bg-gray-50 active:bg-gray-100',

        //This section is only for demonstration purposes. The hover, active, disabled, and focus states are handled by the secondary variant.
        secondaryHover: 'bg-btn-hover-bg border border-btn-border font-normal text-btn-text hover:bg-gray-50 active:bg-gray-100',
        secondaryActive: 'bg-btn-active-tint border border-btn-border font-normal text-btn-text hover:bg-gray-50 active:bg-gray-100',
        secondaryDisabled: 'bg-btn-border border border-btn-border-strong font-normal text-btn-text-disabled hover:bg-btn-border active:bg-btn-border shadow-btn-disabled-inset',
        secondaryFocus: 'bg-white border !border-brand-focus font-normal text-btn-text hover:bg-gray-50 active:bg-gray-100 shadow-btn-focus-ring',
        //Secondary button demonstration section end here

        // Same as secondary but no border (e.g. dropdown/menu-style items inside an already-bordered container).
        secondaryFlat: 'bg-white border-0 font-normal text-btn-text hover:bg-gray-50 active:bg-gray-100',

        // Matches legacy ".btn-secondary" (Invoices/Orders toolbar buttons): border darkens + subtle shadow on hover instead of a bg change.
        secondaryBorderHover: 'bg-white border border-btn-border font-normal text-btn-text hover:border-btn-text hover:shadow-btn-border-hover',

        // Matches the legacy compact ".btn-gray" style (desktop footer/toolbar buttons): #B9B9B9 border, gray-200 hover.
        outline: 'bg-white border border-btn-border-strong font-normal text-btn-text hover:bg-gray-200 active:bg-gray-300',

        //This section is only for demonstration purposes. The hover, active, disabled, and focus states are handled by the outline variant.
        outlineHover: 'bg-gray-200 border border-btn-border-strong font-normal text-btn-text hover:bg-gray-200 active:bg-gray-300',
        outlineActive: 'bg-gray-300 border border-btn-border-strong font-normal text-btn-text hover:bg-gray-200 active:bg-gray-300',
        outlineDisabled: 'bg-btn-border border border-btn-border-strong font-normal text-btn-text-disabled hover:bg-btn-border active:bg-btn-border shadow-btn-disabled-inset',
        outlineFocus: 'bg-white border !border-brand-focus font-normal text-btn-text hover:bg-gray-200 active:bg-gray-300 shadow-btn-focus-ring',
        //Outline button demonstration section end here

        // Matches the legacy ".btn-gray-mobile" style (mobile footer buttons): #D1D1D1 border, gray-100 hover.
        outlineMobile: 'bg-white border border-btn-border font-normal text-btn-text hover:bg-gray-100 active:bg-gray-200',

        tertiary: 'text-btn-text-muted bg-transparent !bg-none border-0 font-normal !shadow-none hover:!bg-gray-50 active:!bg-gray-100',

        // Full-width, left-aligned text action used in proposal delivery options.
        deliveryAction: 'w-full !h-8 justify-start !px-0 !py-1 text-base text-btn-text bg-transparent border-0 font-normal hover:bg-btn-hover-bg active:bg-btn-active-tint',

        //This section is only for demonstration purposes. The hover, active, disabled, and focus states are handled by the tertiary variant.
        tertiaryHover: 'text-btn-hover-text bg-transparent !bg-none border-0 font-normal !shadow-none hover:!bg-gray-50 active:!bg-gray-100',
        tertiaryActive: 'text-btn-hover-text bg-transparent !bg-none border-0 font-normal !shadow-none hover:!bg-gray-50 active:!bg-gray-100',
        tertiaryDisabled: 'text-btn-text-disabled bg-transparent !bg-none border-0 font-normal !shadow-none',
        tertiaryFocus: 'text-btn-text-muted bg-transparent !bg-none border font-normal shadow-btn-focus-ring',
        //Tertiary button demonstration section end here

        highlights: 'bg-brand-highlight border border-brand-highlight font-normal text-btn-text-muted hover:bg-brand-highlight-hover active:bg-brand-highlight-active',

        //This section is only for demonstration purposes. The hover, active, disabled, and focus states are handled by the primary variant.
        highlightsHover: 'bg-brand-highlight-hover border border-brand-highlight-hover font-normal text-btn-text-muted hover:bg-brand-highlight-hover active:bg-brand-highlight-active',
        highlightsActive: 'bg-brand-highlight-hover border border-brand-highlight-hover font-normal text-btn-text-muted hover:bg-brand-highlight-hover active:bg-brand-highlight-active',
        highlightsDisabled: 'bg-btn-border border border-btn-border-strong font-normal text-btn-text-muted hover:bg-btn-border active:bg-btn-border shadow-btn-disabled-inset',
        highlightsFocus: 'bg-brand-highlight border !border-brand-focus font-normal text-btn-text-muted hover:bg-brand-highlight-hover active:bg-brand-highlight-active shadow-btn-focus-ring',


        split: 'bg-brand-primary border border-brand-primary font-medium text-white hover:bg-brand-primary-hover active:bg-brand-primary-active',

        //This section is only for demonstration purposes. The hover, active, disabled, and focus states are handled by the primary variant.
        splitHover: 'bg-brand-primary-hover border border-brand-primary-hover font-medium text-white hover:bg-brand-primary-hover active:bg-brand-primary-active',
        splitActive: 'bg-brand-primary-hover border border-brand-primary-hover font-medium text-white hover:bg-brand-primary-hover active:bg-brand-primary-active',
        splitDisabled: 'bg-btn-border border border-btn-border-strong font-medium text-btn-text-disabled hover:bg-btn-border active:bg-btn-border shadow-btn-disabled-inset',
        splitFocus: 'bg-brand-primary border !border-brand-focus font-medium text-white hover:bg-brand-primary-hover active:bg-brand-primary-active shadow-btn-focus-ring',

    };
    return variants[props.variant];
});

// Size styles
// 'xs' matches the most common compact legacy button (32px height, 8px padding).
// 'custom' applies no height/padding/text classes so the caller's own `class` prop controls sizing without fighting Tailwind's fixed rule order.
const sizeClasses = computed(() => {
    const sizes = {
        xs: 'h-8 px-2 py-1 text-base',
        sm: 'h-[22px] px-3 text-sm',
        md: 'h-[30px] px-4 text-base',
        lg: 'h-[38px] px-6 text-lg',
        xl: 'h-[46px] px-8 text-xl',
        // Matches the legacy mobile footer button box (".btn-gray-mobile"/".btn-submit-mobile").
        mobile: 'px-4 py-3 !rounded-lg text-sm min-h-[44px]',
        // Matches the legacy filter-panel Clear/Apply button box (fixed size, used across Invoices/Orders filter panels).
        filter: 'w-[7.3rem] h-[2.18rem] !rounded-md',
        custom: ''
    };
    const sizeClass = sizes[props.size];
    return props.fullWidth ? `${sizeClass} w-full` : sizeClass;
});

// Disabled styles
// Matches legacy disabled buttons across the app (solid gray bg/text, not a translucent variant color).
const disabledClasses = computed(() =>
    props.disabled
        ? '!bg-btn-border !border-btn-border-strong !text-btn-text-disabled !shadow-none cursor-not-allowed'
        : 'cursor-pointer'
);

// Justify content based on variant
const justifyClasses = computed(() => 
    props.variant === 'split' && (props.leftIcon || props.icon) && props.rightIcon
        ? 'justify-between'
        : props.variant === 'deliveryAction' ? 'justify-start' : 'justify-center'
);

// Combined classes
const buttonClasses = computed(() => [
    'rounded',
    'gap-1',
    'focus:outline-none',
    'transition-colors',
    'duration-200',
    'inline-flex',
    'items-center',
    justifyClasses.value,
    variantClasses.value,
    sizeClasses.value,
    disabledClasses.value
]);
</script>
